import { reportMermaidFailure } from "../../jan/utils/mermaidReporting";
import { normalizeSvgForCapture } from "./inlineMermaidSvgs";

const MERMAID_RE = /```mermaid\n([\s\S]*?)```/g;

/**
 * Capture a Mermaid diagram to a PNG data URI at its full intrinsic size.
 *
 * The on-page wrapper has `overflow-x-auto`, so html2canvas-ing it
 * directly only yields the visible viewport slice for wide diagrams.
 * Instead we read the raw `<svg>`, normalize its dimensions to the
 * viewBox (matching the in-app modal's image-export technique), mount it
 * in an off-screen container, and capture that.
 *
 * html2canvas (rather than the SVG→Image→Canvas path) is required because
 * Mermaid uses `<foreignObject>` for HTML labels; the direct path triggers
 * a tainted-canvas SecurityError on those.
 *
 * Returns null if the wrapper has no `<svg>` child or normalization can't
 * determine a usable size — caller falls back to the original code fence
 * so the user still sees something rather than a broken image link.
 *
 * Used only by the DOCX export path; the PDF flow inlines SVG data URIs
 * via inlineMermaidSvgs.ts so the server-side Puppeteer pipeline can
 * render diagrams at native resolution without the rasterization round
 * trip and the resulting payload bloat.
 */
const captureDiagramToPngDataUri = async (
  wrapper: HTMLElement
): Promise<string | null> => {
  const svgEl = wrapper.querySelector("svg");
  if (!svgEl) return null;

  const { svg, width, height } = normalizeSvgForCapture(svgEl.outerHTML);
  if (width <= 0 || height <= 0) return null;

  const offscreen = document.createElement("div");
  offscreen.style.position = "fixed";
  offscreen.style.left = "-99999px";
  offscreen.style.top = "0";
  offscreen.style.width = `${width}px`;
  offscreen.style.height = `${height}px`;
  offscreen.style.background = "#ffffff";
  offscreen.innerHTML = svg;
  document.body.appendChild(offscreen);

  try {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(offscreen, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
    });
    return canvas.toDataURL("image/png");
  } finally {
    document.body.removeChild(offscreen);
  }
};

/**
 * Replace each ```mermaid block in the markdown with a PNG data-URI image
 * reference, captured from the already-rendered diagrams in `root`.
 *
 * Order matters: the i-th `.mermaid-diagram` element in `root` corresponds
 * to the i-th ```mermaid block in the markdown, because `PublicMessageBubble`
 * renders segments in document order.
 *
 * Returns the markdown unchanged when there are no mermaid blocks or no
 * root element. Individual blocks that fail to capture fall back to the
 * original code fence so the user still sees something.
 */
export const transformMermaidBlocksToImages = async (
  markdown: string,
  root: HTMLElement | null
): Promise<string> => {
  const blocks = [...markdown.matchAll(MERMAID_RE)];
  if (blocks.length === 0 || !root) return markdown;

  const diagramEls = Array.from(
    root.querySelectorAll<HTMLElement>(".mermaid-diagram")
  );

  const dataUris = await Promise.all(
    blocks.map(async (_, i) => {
      const el = diagramEls[i];
      if (!el || !el.querySelector("svg")) {
        reportMermaidFailure("public-export", "missing-diagram");
        return null;
      }
      try {
        const dataUri = await captureDiagramToPngDataUri(el);
        if (!dataUri) {
          reportMermaidFailure("public-export", "capture");
        }
        return dataUri;
      } catch (error) {
        reportMermaidFailure("public-export", "capture", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        console.error("Failed to capture mermaid diagram:", error);
        return null;
      }
    })
  );

  // Splice back-to-front so earlier match indices stay valid.
  let out = markdown;
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    const dataUri = dataUris[i];
    const start = block.index ?? 0;
    const end = start + block[0].length;
    const replacement = dataUri ? `![Mermaid diagram](${dataUri})` : block[0];
    out = out.slice(0, start) + replacement + out.slice(end);
  }
  return out;
};
